<template>
  <div :class="['card-chart', { fullscreen: dashboardFullscreen }]" :style="{ height: cardHeight }">
    <CardInteractive
      :title="title"
      :info="info"
      :enableFullscreen="enableFullscreen"
      :showControls="fullscreen && enableResolution"
      :isLoading="!components"
      headerDivider
      @toggleFullscreen="toggleFullscreen"
      @toggleShowInfo="(value) => (showingInfo = value)"
    >
      <template #controls>
        <UnderlineTabGroup
          tabStyle="'pillTabSmall'"
          :size="'small'"
          :buttons="buttons"
          :selectedName="selectedName"
        />
      </template>
      <ChartBase ref="chartBase" :height="currentHeight" :components="filteredComponents" />
      <template v-if="legendComponents.length > 1" #header>
        <div class="checkbox-row">
          <LegendBase v-if="fullscreen" :components="legendComponentsLarge" @change="onChecked" />
          <LegendBase v-else :components="legendComponents" @change="onChecked" />
        </div>
      </template>
    </CardInteractive>
  </div>
</template>

<script lang="ts" setup>
import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { computed, inject, onUnmounted, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import { Chart, ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import LegendBase from './LegendBase/LegendBase.vue'
import { DataSource, useDataSources } from '@/composables/useDataSources'
import { useI18n } from 'vue-i18n'
import {
  compileChart,
  createChartLegendComponents,
  filterChartComponents
} from './ChartComponent/chart/chart'
import { FeatureType, useCurrentFeatureFilters, useFilters } from '@/composables/useFilters'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { calculateBucketSizes } from '@/utils/calculateBucketSizes'
import { sharedDataKey } from '../../types'

export type Props = {
  title: string
  info?: string
  enableFullscreen?: boolean
  chart: Chart
  chartHeight?: string
  cardHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  chartHeight: '100%',
  cardHeight: '400px',
  info: undefined
})
const sharedData = inject(sharedDataKey)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const { chart, chartHeight } = toRefs(props)

const { locale } = useI18n()

const { dataSources } = toRefs(useDataSources())

const showingInfo = ref(false)

const currentFeature = useCurrentFeatureFilters()
const enableResolution = computed(
  () =>
    chart.value.type === 'timeseries' &&
    currentFeature.value?.$id !== 'building_score' &&
    currentFeature.value?.$id !== 'utilization' &&
    currentFeature.value?.$id !== FeatureType.TicketAnalysis
)
const chartBase = ref<InstanceType<typeof ChartBase> | undefined>(undefined)
const viewport = ref<DOMRect | undefined>(undefined)

const components = computed<ChartComponent[] | undefined>(() =>
  viewport.value
    ? compileChart(
        chart.value,
        dataSources.value as Record<string, DataSource<any> | undefined>,
        viewport.value,
        locale.value
      )
    : undefined
)

const filteredComponents = computed<ChartComponent[]>(() =>
  filterChartComponents(chart.value.type, components.value ?? [], enabledLabels.value)
)

const legendComponents = computed(() =>
  createChartLegendComponents(props.chart, locale.value, {
    type: 'tiny'
  })
)
const legendComponentsLarge = computed(() =>
  createChartLegendComponents(props.chart, locale.value, {
    type: 'label'
  })
)

const enabledLabels = ref<Set<string>>(new Set())

const fullscreen = ref(false)

const currentHeight = computed(() => (fullscreen.value ? '600px' : chartHeight.value))

const updateViewport = () => {
  viewport.value = chartBase.value?.$el.getBoundingClientRect()
}

const viewportObserver = new ResizeObserver(updateViewport)

const filters = useFilters(FeatureType.IndoorClimate)
const resolutions: Partial<Record<BucketSize, { en: string; sv: string }>> = {
  MAX: { en: 'Year', sv: 'År' },
  YEAR: { en: 'Year', sv: 'År' },
  QUARTER: { en: 'Quarter', sv: 'Kvartal' },
  MONTH: { en: 'Month', sv: 'Månad' },
  WEEK: { en: 'Week', sv: 'Vecka' },
  DAY: { en: 'Day', sv: 'Dag' },
  HOUR: { en: 'Hour', sv: 'Timme' }
}

const selectedName = computed<string | undefined>(() =>
  filters.current.bucketSize ? resolutions[filters.current.bucketSize]?.[locale.value] : undefined
)
const buttons = ref<
  {
    text: string
    name: string
    onClick: () => void
  }[]
>([])

watch(
  () => filters.current.calendar,
  (value) => {
    if (!value?.from || !value.to) return
    buttons.value = calculateBucketSizes(value.from, value.to)
      .filter((x) => resolutions[x] !== undefined)
      .map((resolution) => {
        return {
          text: resolutions[resolution]![locale.value],
          name: resolutions[resolution]![locale.value],
          onClick: () => {
            filters.current.bucketSize = resolution
            filters.submit()
          }
        }
      })
  },
  { immediate: true }
)

onUnmounted(() => {
  viewportObserver.disconnect()
  filters.submit()
})

watch(
  chartBase,
  () => {
    viewportObserver.disconnect()
    if (chartBase.value) {
      updateViewport()
      viewportObserver.observe(chartBase.value.$el)
    }
  },
  { immediate: true }
)

watch(
  legendComponents,
  () => {
    enabledLabels.value = new Set(legendComponents.value?.map((c) => c.label) ?? [])
  },
  { immediate: true }
)

function onChecked(label: string, checked: boolean) {
  if (checked) {
    enabledLabels.value.add(label)
  } else {
    enabledLabels.value.delete(label)
  }
}

function toggleFullscreen(value: boolean) {
  fullscreen.value = value
  if (!value) {
    filters.resetBucketSize()
    filters.submit()
  }
}
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';

@media screen and (min-width: 768px) {
  .card-chart {
    width: 100%;

    &.fullscreen {
      width: calc(50% - variables.$gap-large);
    }
  }
}

.checkbox-row {
  display: flex;
  gap: variables.$gap-xlarge;
  justify-content: left;
  flex-wrap: wrap;
}
</style>
